import { api } from "../Components/functions/Api";

/**
 * Durable outbox for Coded Estate trade-tracking events.
 *
 * WHY THIS EXISTS
 * The points leaderboard counts ONLY trades placed through the Coded Estate UI,
 * attributed by the tx hashes we record via `/tracking/trade-event`. If a trade's
 * event fails to reach the backend (tab closed mid-request, offline, transient
 * network error), that trade can never be attributed — so capture must be durable.
 *
 * HOW IT WORKS
 *   1. enqueueTradeEvent() writes the event to localStorage SYNCHRONOUSLY the moment
 *      we have the tx hash. This is instant (microseconds) and survives a tab close,
 *      so the event is never lost even if the user navigates away immediately.
 *   2. flushOutbox() drains the queue by POSTing via the normal (encrypted, authed)
 *      api() path. Successfully-sent events are removed; failures stay queued.
 *   3. The flusher runs on import, on a timer, and whenever the tab regains focus /
 *      comes back online — so anything stranded gets retried automatically.
 *
 * The backend dedupes on txHash (unique index), so re-sending the same event is safe.
 *
 * Why not navigator.sendBeacon? This backend expects an AES-encrypted `{payload}`
 * body and a JWT Authorization header; sendBeacon can't set that header, so it would
 * be rejected. A synchronous localStorage write gives the same "survives unload"
 * guarantee while reusing the existing api() contract.
 */

const OUTBOX_KEY = "ce_tracking_outbox_v1";
const FLUSH_INTERVAL_MS = 30 * 1000;

function readQueue() {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(queue));
  } catch (_) {
    // localStorage full/unavailable — nothing more we can do client-side.
  }
}

/**
 * Synchronously persist a trade event, then kick off a (non-blocking) flush.
 * Call this the instant a tx hash is known. De-duplicates by txHash.
 */
export function enqueueTradeEvent(event) {
  if (!event || !event.txHash) return;
  const queue = readQueue();
  if (queue.some((e) => e.txHash === event.txHash)) return; // already queued
  queue.push({ ...event, _queuedAt: Date.now() });
  writeQueue(queue);
  // Fire-and-forget; the durable copy is already safe in localStorage.
  flushOutbox();
}

let flushing = false;

/** Attempt to send every queued event. Removes only those that succeed. */
export async function flushOutbox() {
  if (flushing) return;
  const queue = readQueue();
  if (!queue.length) return;
  flushing = true;
  try {
    const remaining = [];
    for (const event of queue) {
      try {
        const { _queuedAt, ...payload } = event;
        await api("tracking/trade-event", payload);
        // success → drop from queue
      } catch (_) {
        remaining.push(event); // keep for next flush
      }
    }
    writeQueue(remaining);
  } finally {
    flushing = false;
  }
}

/** Start background retries. Idempotent — safe to call from multiple entry points. */
let started = false;
export function startOutboxFlusher() {
  if (started || typeof window === "undefined") return;
  started = true;
  flushOutbox();
  setInterval(flushOutbox, FLUSH_INTERVAL_MS);
  window.addEventListener("online", flushOutbox);
  window.addEventListener("focus", flushOutbox);
}
